import React, { useState, useEffect } from "react";
import axios from "axios";
import { Trophy, TrendingUp, TrendingDown, Users, Loader2, Zap, CheckCircle2 } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, ResponsiveContainer } from "recharts";
import { API } from "@/App";
import { useI18n } from "@/i18n";
import { TableExportToolbar } from "@/components/TableExportToolbar";

export default function Leaderboard() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState(null);
  const [historyDays, setHistoryDays] = useState(90);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      // Fetch leaderboard first (critical). History is best-effort — chart hides if it fails.
      try {
        const r1 = await axios.get(`${API}/admin/leaderboard`);
        setData(r1.data);
      } catch (e) {
        setError(e.response?.data?.detail || "Failed to load leaderboard");
        setLoading(false);
        return;
      }
      try {
        const r2 = await axios.get(`${API}/admin/leaderboard/history?days=${historyDays}`);
        setHistory(r2.data);
      } catch {
        // History endpoint may not be deployed yet — chart will simply not render
        setHistory(null);
      }
      setLoading(false);
    })();
  }, [historyDays]);

  if (loading) return <div className="flex items-center justify-center h-full min-h-[60vh]"><Loader2 className="animate-spin text-[#00FF88]" size={32} /></div>;
  if (error) return <div className="p-6 text-[#FF3366] font-mono text-sm">{error}</div>;
  if (!data) return null;

  const { leaderboard, totals } = data;
  const medal = (i) => i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i + 1}`;
  const fmtMoney = (v) => v >= 0 ? `+$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : `-$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—";

  return (
    <div data-testid="leaderboard-page" className="animate-fade-in max-w-6xl">
      <div className="mb-6">
        <h2 className="text-3xl font-black tracking-tighter text-white font-['Chivo'] flex items-center gap-3">
          <Trophy size={26} className="text-[#FFB800]" /> {t("leaderboard.title")}
        </h2>
        <p className="text-sm text-[#8A8A93] mt-1">{t("leaderboard.subtitle")}</p>
      </div>

      {/* Family totals */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        <StatCard label="Members" value={totals.total_users} icon={<Users size={14} />} />
        <StatCard label="Active Cycles" value={totals.total_open} icon={<Zap size={14} className="text-[#FFB800]" />} />
        <StatCard label="Closed Cycles" value={totals.total_closed} icon={<CheckCircle2 size={14} className="text-[#00FF88]" />} />
        <StatCard
          label="Total Realized P&L"
          value={fmtMoney(totals.total_realized_pl_dollars)}
          mono
          tone={totals.total_realized_pl_dollars >= 0 ? "good" : "bad"}
        />
      </div>

      {/* Cumulative P&L chart over time */}
      {history && (
        <div data-testid="leaderboard-chart" className="bg-[#121216] border border-[#26262B] rounded-sm p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-white">Cumulative P&L over time</h3>
              <p className="text-[10px] text-[#8A8A93] font-mono mt-0.5">Realized P&L from closed cycles per family member ($)</p>
            </div>
            <div className="flex gap-1">
              {[30, 90, 180, 365].map(d => (
                <button key={d} onClick={() => setHistoryDays(d)}
                  className={`text-[10px] font-mono px-2.5 py-1 rounded-sm border ${historyDays === d ? "border-[#00FF88] text-[#00FF88] bg-[#00FF88]/10" : "border-[#26262B] text-[#8A8A93] hover:text-white"}`}>
                  {d}d
                </button>
              ))}
            </div>
          </div>
          {history.series.length === 0 || history.users.every(u => u.final_pl === 0) ? (
            <div className="h-[260px] flex items-center justify-center text-[#5A5A65] text-xs font-mono">
              No closed cycles in the last {historyDays} days yet — chart will populate as members close trades.
            </div>
          ) : (
            <PerformanceChart series={history.series} users={history.users} />
          )}
        </div>
      )}

      {/* Leaderboard table */}
      <div className="bg-[#121216] border border-[#26262B] rounded-sm overflow-hidden">
        {/* Iter116.6 — Bulk export of the family leaderboard for quarterly
            reviews. Same UX as Decision Journal / Performance — admin can
            paste a chat summary or pull the per-member numbers into a
            spreadsheet without leaving the page. */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-[#26262B] bg-[#08080A]">
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A93]">
            Family Standings
          </div>
          <TableExportToolbar
            rows={leaderboard}
            label="Family Leaderboard Export"
            csvFilename="leaderboard"
            buildLine={(r) => {
              const parts = [r.name || r.email, r.role];
              if (r.closed_cycles != null) parts.push(`${r.closed_cycles}C/${r.open_cycles}O`);
              if (r.win_rate != null) parts.push(`win ${r.win_rate}%`);
              if (r.avg_roi != null) parts.push(`avg ROI ${r.avg_roi}%`);
              parts.push(`realized ${fmtMoney(r.realized_pl_dollars)}`);
              if (r.unrealized_pl_dollars) parts.push(`unrealized +$${r.unrealized_pl_dollars}`);
              return parts.join(" · ");
            }}
            csvColumns={[
              { label: "rank",                 get: (_, i) => i + 1 },
              { label: "name",                 get: r => r.name || "" },
              { label: "email",                get: r => r.email || "" },
              { label: "role",                 get: r => r.role || "" },
              { label: "total_cycles",         get: r => r.total_cycles ?? "" },
              { label: "closed_cycles",        get: r => r.closed_cycles ?? "" },
              { label: "open_cycles",          get: r => r.open_cycles ?? "" },
              { label: "winning_cycles",       get: r => r.winning_cycles ?? "" },
              { label: "losing_cycles",        get: r => r.losing_cycles ?? "" },
              { label: "win_rate_pct",         get: r => r.win_rate ?? "" },
              { label: "avg_roi_pct",          get: r => r.avg_roi ?? "" },
              { label: "realized_pl_dollars",  get: r => r.realized_pl_dollars ?? "" },
              { label: "realized_pl_per_share", get: r => r.realized_pl_per_share ?? "" },
              { label: "unrealized_pl_dollars", get: r => r.unrealized_pl_dollars ?? "" },
              { label: "last_activity",        get: r => r.last_activity || "" },
            ]}
            testidPrefix="leaderboard"
          />
        </div>
        <div className="grid grid-cols-[60px_1fr_120px_120px_100px_140px_140px] gap-3 px-4 py-3 border-b border-[#26262B] text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A93]">
          <div>Rank</div>
          <div>Member</div>
          <div className="text-right">Closed / Open</div>
          <div className="text-right">Win Rate</div>
          <div className="text-right">Avg ROI</div>
          <div className="text-right">Realized P&L</div>
          <div className="text-right">Unrealized</div>
        </div>
        {leaderboard.length === 0 && (
          <div className="p-8 text-center text-[#8A8A93] text-sm">No family members yet. Add users from Settings → Users.</div>
        )}
        {leaderboard.map((row, i) => (
          <div key={row.user_id} data-testid={`leaderboard-row-${row.email}`}
            className="grid grid-cols-[60px_1fr_120px_120px_100px_140px_140px] gap-3 px-4 py-4 border-b border-[#1A1A20] last:border-b-0 items-center hover:bg-[#1A1A20]/50">
            <div className="text-xl">{medal(i)}</div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-white truncate" title={row.email}>{row.name || row.email}</p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={`text-[8px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-sm ${row.role === "admin" ? "bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20" : "bg-[#FFB800]/10 text-[#FFB800] border border-[#FFB800]/20"}`}>
                  {row.role}
                </span>
                <span className="text-[10px] text-[#8A8A93] font-mono truncate" title={row.email}>{row.email}</span>
              </div>
              {row.last_activity && (
                <p className="text-[10px] text-[#5A5A65] font-mono mt-0.5">Last: {fmtDate(row.last_activity)}</p>
              )}
            </div>
            <div className="text-right font-mono">
              <p className="text-sm text-white">{row.closed_cycles}<span className="text-[#5A5A65]"> / </span><span className="text-[#FFB800]">{row.open_cycles}</span></p>
              <p className="text-[9px] text-[#8A8A93] uppercase tracking-wider">{row.total_cycles} total</p>
            </div>
            <div className="text-right font-mono">
              {row.win_rate != null ? (
                <>
                  <p className={`text-sm ${row.win_rate >= 60 ? "text-[#00FF88]" : row.win_rate >= 40 ? "text-[#FFB800]" : "text-[#FF3366]"}`}>{row.win_rate}%</p>
                  <p className="text-[9px] text-[#8A8A93]">{row.winning_cycles}W · {row.losing_cycles}L</p>
                </>
              ) : <span className="text-[#5A5A65] text-xs">—</span>}
            </div>
            <div className="text-right font-mono text-sm text-[#E0E0E0]">
              {row.avg_roi != null ? `${row.avg_roi}%` : <span className="text-[#5A5A65]">—</span>}
            </div>
            <div className={`text-right font-mono text-sm font-bold ${row.realized_pl_dollars >= 0 ? "text-[#00FF88]" : "text-[#FF3366]"}`}>
              {fmtMoney(row.realized_pl_dollars)}
              <p className="text-[9px] text-[#8A8A93] font-normal mt-0.5">${row.realized_pl_per_share}/sh</p>
            </div>
            <div className="text-right font-mono text-sm text-[#FFB800]">
              {row.unrealized_pl_dollars > 0 ? `+$${row.unrealized_pl_dollars.toLocaleString()}` : <span className="text-[#5A5A65]">—</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Best & Worst trades */}
      {leaderboard.some(r => r.best_trade) && (
        <div className="mt-8 grid grid-cols-2 gap-4">
          <BestWorstCard label="Top Trades" tone="good" rows={leaderboard.filter(r => r.best_trade).map(r => ({ name: r.name || r.email, ...r.best_trade }))} />
          <BestWorstCard label="Worst Trades" tone="bad" rows={leaderboard.filter(r => r.worst_trade).map(r => ({ name: r.name || r.email, ...r.worst_trade }))} />
        </div>
      )}

      <p className="text-[10px] text-[#5A5A65] font-mono mt-6">
        Notes: P&L assumes 1 contract = 100 shares. Unrealized = premium collected on open positions. Closed cycles include CSP-expired and CC-assigned outcomes.
      </p>
    </div>
  );
}

function PerformanceChart({ series, users }) {
  // Distinct colors per user — cycles through this palette
  const palette = ["#00FF88", "#FFB800", "#007AFF", "#FF3366", "#B47AEA", "#22D3EE", "#F97316"];
  // Filter to users who actually have non-zero activity to keep the chart readable
  const activeUsers = users.filter(u => u.final_pl !== 0);
  const fmtTick = (d) => {
    try {
      const dt = new Date(d);
      return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch { return d; }
  };
  return (
    <div style={{ width: "100%", height: 260 }}>
      <ResponsiveContainer>
        <LineChart data={series} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#26262B" />
          <XAxis dataKey="date" tickFormatter={fmtTick} stroke="#5A5A65" tick={{ fontSize: 10, fontFamily: "monospace" }} />
          <YAxis stroke="#5A5A65" tick={{ fontSize: 10, fontFamily: "monospace" }} tickFormatter={(v) => `$${v}`} />
          <RTooltip
            contentStyle={{ background: "#08080A", border: "1px solid #26262B", borderRadius: 2, fontSize: 11, fontFamily: "monospace" }}
            labelStyle={{ color: "#8A8A93" }}
            itemStyle={{ color: "#fff" }}
            formatter={(value) => [`$${Number(value).toLocaleString()}`, ""]}
          />
          <Legend wrapperStyle={{ fontSize: 11, fontFamily: "monospace" }} />
          {activeUsers.map((u, i) => (
            <Line
              key={u.user_id}
              type="monotone"
              dataKey={u.name}
              stroke={palette[i % palette.length]}
              strokeWidth={2}
              dot={{ r: 2 }}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function StatCard({ label, value, icon, mono, tone }) {
  const toneClass = tone === "good" ? "text-[#00FF88]" : tone === "bad" ? "text-[#FF3366]" : "text-white";
  return (
    <div className="bg-[#121216] border border-[#26262B] rounded-sm p-4">
      <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-1">
        {icon}{label}
      </div>
      <p className={`text-xl font-black ${mono ? "font-mono" : ""} ${toneClass}`}>{value}</p>
    </div>
  );
}

function BestWorstCard({ label, tone, rows }) {
  const sorted = [...rows].sort((a, b) => tone === "good" ? (b.pl_cycle || 0) - (a.pl_cycle || 0) : (a.pl_cycle || 0) - (b.pl_cycle || 0)).slice(0, 5);
  const Icon = tone === "good" ? TrendingUp : TrendingDown;
  const toneColor = tone === "good" ? "text-[#00FF88]" : "text-[#FF3366]";
  return (
    <div className="bg-[#121216] border border-[#26262B] rounded-sm p-4">
      <h3 className={`text-[10px] font-bold uppercase tracking-[0.15em] mb-3 flex items-center gap-1.5 ${toneColor}`}>
        <Icon size={12} /> {label}
      </h3>
      <div className="space-y-2">
        {sorted.map((r, i) => (
          <div key={i} className="flex items-center justify-between text-xs font-mono">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-[#8A8A93] truncate">{r.name}</span>
              <span className="text-white font-bold">{r.symbol}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-[#8A8A93]">{r.roi_cycle != null ? `${r.roi_cycle}%` : ""}</span>
              <span className={`font-bold ${(r.pl_cycle || 0) >= 0 ? "text-[#00FF88]" : "text-[#FF3366]"}`}>
                {(r.pl_cycle || 0) >= 0 ? "+" : ""}${(r.pl_cycle * 100).toFixed(0)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


